export const applyTheme = (theme) => {
  const root = document.documentElement;
  
  if (theme === 'auto') {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    root.setAttribute('data-theme', prefersDark ? 'dark' : 'light');
  } else {
    root.setAttribute('data-theme', theme);
  }
};

export const adjustFontSize = (size) => {
  const root = document.documentElement;
  root.className = root.className.replace(/font-size-\w+/g, '');
  root.classList.add(`font-size-${size}`);
};

export const adjustFontFamily = (family) => {
  const root = document.documentElement;
  root.className = root.className.replace(/font-\w+/g, '');
  root.classList.add(`font-${family}`);
};

export const toggleHighContrast = (enabled) => {
  const root = document.documentElement;
  
  if (enabled) {
    root.setAttribute('data-theme', 'high-contrast');
  } else {
    const currentTheme = root.getAttribute('data-theme');
    if (currentTheme === 'high-contrast') {
      applyTheme('auto');
    }
  }
};

export const respectReducedMotion = () => {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const root = document.documentElement;
  
  if (prefersReducedMotion) {
    root.style.setProperty('--animation-duration', '0.01ms');
    root.style.setProperty('--transition-duration', '0.01ms');
  }
  
  return prefersReducedMotion;
};

let liveRegion = null;

export const announceToScreenReader = (message, priority = 'polite') => {
  if (!liveRegion) {
    liveRegion = document.createElement('div');
    liveRegion.setAttribute('aria-live', priority);
    liveRegion.setAttribute('aria-atomic', 'true');
    liveRegion.className = 'sr-only';
    document.body.appendChild(liveRegion);
  }
  
  liveRegion.setAttribute('aria-live', priority);
  liveRegion.textContent = message;
  
  setTimeout(() => {
    if (liveRegion) {
      liveRegion.textContent = '';
    }
  }, 1000);
};

export const setupFocusManagement = () => {
  let lastFocusedElement = null;
  
  const trapFocus = (container) => {
    const focusableElements = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    if (focusableElements.length === 0) return;
    
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    
    const handleKeyDown = (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
      
      if (e.key === 'Escape') {
        restoreFocus();
        container.removeEventListener('keydown', handleKeyDown);
      }
    };
    
    container.addEventListener('keydown', handleKeyDown);
    firstElement.focus();
    
    return () => {
      container.removeEventListener('keydown', handleKeyDown);
    };
  };
  
  const saveFocus = () => {
    lastFocusedElement = document.activeElement;
  };
  
  const restoreFocus = () => {
    if (lastFocusedElement && lastFocusedElement.focus) {
      lastFocusedElement.focus();
    }
  };
  
  return { trapFocus, saveFocus, restoreFocus };
};

export const createSkipLink = (targetId, text = 'Skip to main content') => {
  const skipLink = document.createElement('a');
  skipLink.href = `#${targetId}`;
  skipLink.textContent = text;
  skipLink.className = 'sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-primary focus:text-white focus:px-4 focus:py-2 focus:rounded';
  
  skipLink.addEventListener('click', (e) => {
    e.preventDefault();
    const target = document.getElementById(targetId);
    if (target) {
      target.focus();
      target.scrollIntoView({ behavior: 'smooth' });
    }
  });
  
  return skipLink;
};

export const enhanceFormAccessibility = (form) => {
  const inputs = form.querySelectorAll('input, select, textarea');
  
  inputs.forEach(input => {
    const label = form.querySelector(`label[for="${input.id}"]`);
    
    if (!label && !input.getAttribute('aria-label') && !input.getAttribute('aria-labelledby')) {
      console.warn('Input element missing label:', input);
    }
    
    if (input.hasAttribute('required')) {
      input.setAttribute('aria-required', 'true');
      
      if (label) {
        const requiredIndicator = document.createElement('span');
        requiredIndicator.textContent = '*';
        requiredIndicator.setAttribute('aria-label', 'required');
        requiredIndicator.className = 'text-danger ml-1';
        label.appendChild(requiredIndicator);
      }
    }
    
    const errorElement = form.querySelector(`[data-error-for="${input.id}"]`);
    if (errorElement) {
      input.setAttribute('aria-describedby', errorElement.id);
    }
  });
};

export const validateColorContrast = (foreground, background) => {
  const getLuminance = (color) => {
    const rgb = color.match(/\d+/g);
    if (!rgb) return 0;
    
    const [r, g, b] = rgb.map(c => {
      c = c / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };
  
  const fgLum = getLuminance(foreground);
  const bgLum = getLuminance(background);
  
  const contrast = (Math.max(fgLum, bgLum) + 0.05) / (Math.min(fgLum, bgLum) + 0.05);
  
  return {
    ratio: contrast,
    passAA: contrast >= 4.5,
    passAAA: contrast >= 7,
    passLarge: contrast >= 3
  };
};

export const addAriaLiveRegion = (id = 'aria-live-region') => {
  if (document.getElementById(id)) return;
  
  const region = document.createElement('div');
  region.id = id;
  region.setAttribute('aria-live', 'polite');
  region.setAttribute('aria-atomic', 'false');
  region.className = 'sr-only';
  document.body.appendChild(region);
  
  return region;
};

export const initializeAccessibility = (settings) => {
  applyTheme(settings.accessibility.theme);
  adjustFontSize(settings.accessibility.fontSize);
  adjustFontFamily(settings.accessibility.fontFamily);
  toggleHighContrast(settings.accessibility.highContrast);
  respectReducedMotion();
  addAriaLiveRegion();
  
  const mainContent = document.getElementById('main-content');
  if (mainContent) {
    const skipLink = createSkipLink('main-content');
    document.body.insertBefore(skipLink, document.body.firstChild);
  }
  
  document.addEventListener('DOMContentLoaded', () => {
    const forms = document.querySelectorAll('form');
    forms.forEach(enhanceFormAccessibility);
  });
};